import React, { useEffect, useRef, useState } from "react";
import productAPI from "../../api/productApi";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, SearchX, ChevronRight } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

// variant product ho to pehle variant ka price lo
const getPricing = (item) => {
  const src =
    item?.productType === "Variant" && item?.variants?.length
      ? item.variants[0]
      : item;
  const price = Number(src?.price ?? 0);
  const mrp = Number(src?.mrp ?? 0);
  const discount = Number(src?.discount ?? 0);
  return { price, mrp, discount, hasDiscount: mrp > price && price > 0 };
};

// offcanvas ko code se band karo
const closeOffcanvas = () => {
  const el = document.getElementById("searchOffcanvas");
  if (!el) return;

  const instance = window.bootstrap?.Offcanvas?.getInstance(el);
  if (instance) {
    instance.hide();
  } else {
    // bootstrap global na mile to close button ko click kar do
    el.querySelector(".search-close-btn")?.click();
  }
};

function SearchCanvas({ searchText, setSearchText }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const reqId = useRef(0); // purani request ka response ignore karne ke liye

  const query = (searchText || "").trim();

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchProducts(query);
    }, 350);

    return () => clearTimeout(delay);
  }, [query]);

  const fetchProducts = async (text) => {
    const myReq = ++reqId.current;
    setLoading(true);

    try {
      const res = await productAPI.listing({
        page: 1,
        limit: 6,
        // backend jo bhi naam use karta ho — teeno bhej rahe hain
        ...(text ? { keyword: text, search: text, q: text } : {}),
      });

      // naya search shuru ho chuka hai to ye response chhod do
      if (myReq !== reqId.current) return;

      if (res.code === 200) {
        setProducts(res.data?.products || []);
      } else {
        setProducts([]);
      }
    } catch (err) {
      if (myReq !== reqId.current) return;
      console.error("Error loading products:", err);
      setProducts([]);
    } finally {
      if (myReq === reqId.current) setLoading(false);
    }
  };

  // link click: pehle offcanvas band, phir page pe navigate
  const goTo = (e, path) => {
    e.preventDefault();
    closeOffcanvas();
    navigate(path);
  };

  return (
    <div className="salty-search-box">
      {/* SEARCH INPUT */}
      <div className="salty-search-input-wrap">
        <Search size={20} strokeWidth={1.8} className="salty-search-icon" />
        <input
          type="text"
          autoFocus
          className="salty-search-input"
          placeholder="Search for products…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        {searchText && (
          <button
            type="button"
            className="salty-search-clear"
            aria-label="Clear search"
            onClick={() => setSearchText("")}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* RESULTS */}
      <div className="salty-search-results">
        <div>
          <div className="salty-search-head">
            <h6>
              {query ? (
                <>
                  Results for <span>“{query}”</span>
                </>
              ) : (
                "Popular products"
              )}
            </h6>
            {!loading && (
              <small>
                {products.length} {products.length === 1 ? "item" : "items"}
              </small>
            )}
          </div>

          {loading ? (
            <div className="salty-search-grid">
              {[...Array(6)].map((_, i) => (
                <div className="salty-search-card skeleton" key={i}>
                  <div className="salty-search-card-img" />
                  <div className="sk-line" />
                  <div className="sk-line short" />
                </div>
              ))}
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="salty-search-grid">
                {products.map((item) => {
                  const { price, mrp, discount, hasDiscount } =
                    getPricing(item);
                  const path = `/shop/${item.slug}`;

                  return (
                    <Link
                      key={item._id}
                      to={path}
                      className="salty-search-card"
                      onClick={(e) => goTo(e, path)}
                    >
                      <div className="salty-search-card-img">
                        {hasDiscount && discount > 0 && (
                          <span className="salty-sale-badge">
                            {Math.round(discount)}% OFF
                          </span>
                        )}
                        <img
                          src={item.thumbnail}
                          alt={item.title}
                          loading="lazy"
                        />
                      </div>
                      <h6 className="salty-search-card-title">{item.title}</h6>
                      <p className="salty-search-card-price">
                        <span className="salty-price-final">₹{fmt(price)}</span>
                        {hasDiscount && (
                          <span className="salty-price-mrp">₹{fmt(mrp)}</span>
                        )}
                      </p>
                    </Link>
                  );
                })}
              </div>

              <Link
                to="/shop"
                className="salty-search-viewall"
                onClick={(e) => goTo(e, "/shop")}
              >
                View all products <ChevronRight size={14} />
              </Link>
            </>
          ) : (
            <div className="salty-search-empty">
              <SearchX size={40} strokeWidth={1.4} />
              <p className="salty-search-status">No products found</p>
              <small>We don't have this product...</small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SearchCanvas;